<template>
  <!-- Page Content -->
  <div class="whole-page" id="whole-page-div">
    <AIChatFloat :colorTheme="colorTheme" />
    <div class="navbar-div">
      <NavbarComponent
        :colorTheme="colorTheme"
        :user="user"
        analysis="ANALYTICS"
        :selectedNavLink="selectedNavLink"
        @eventOnUserSelectNavLink="eventOnClientSelect"
        @eventOnUserColorTheme="onUserColorTheme"
      />
    </div>
    <div class="h-100 d-flex flex-column">
      <!-- Link 0 selected / Currency -->
      <div class="row f-row h-100" v-if="selectedNavLink === 0">
        <div class="col col-12 h-100">
          <Currency
            :analyticsPreferences="analyticsPreferences"
            :colorTheme="colorTheme"
            :companies="companies"
            :companyParametersReady="companyParametersReady"
            :countrySpecs="countrySpecs"
            :currencySpecs="currencySpecs"
            :optionTypes="optionTypes"
            :processData="processData"
          />
        </div>
      </div>
      <!-- Link 1 selected / Commodity -->
      <div class="row f-row h-100" v-if="selectedNavLink === 1">
        <div class="col col-12 h-100">
          <Commodity
            :analyticsPreferences="analyticsPreferences"
            :colorTheme="colorTheme"
            :commoditySpecs="commoditySpecs"
            :companies="companies"
            :companyParametersReady="companyParametersReady"
            :countrySpecs="countrySpecs"
            :optionTypes="optionTypes"
            :processData="processData"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AIChatFloat from "@/components/ai/AIChatFloat.vue";
import Commodity from "@/components/analytics/Commodity.vue";
import Currency from "@/components/analytics/Currency.vue";
import NavbarComponent from "@/components/Navbar.vue";
import {
  assetSelectionVuex,
  colorThemeChange,
  colorThemeVuex,
} from "@/_core/_helpers/_helpers";
import { getCompanyParameters } from "@/_core/data/Overview";
import { getOptionTypesData } from "@/_core/data/api_calls/getOptionTypesData";
import { ProcessData } from "@/_core/data/ProcessData";
import { saveUserPreferences } from "@/_core/data/SaveToDB";
import { initAnalyticsPreferences } from "../_core/data/Analytics/AnalyticsGeneral";

export default {
  name: "Analytics",
  components: {
    AIChatFloat,
    Currency,
    Commodity,
    NavbarComponent,
  },
  data() {
    return {
      allAnalysis: [
        "valuation",
        "futures",
        "date",
        "clients_management",
        "providers_management",
        "scenario",
        "strategies",
        "derivatives",
        "providers_scenarios",
        "clients_scenarios",
        "counterparty",
      ],
      analyticsPreferences: {},
      analysis: 0,
      assets: ["currency", "commodity"],
      colorTheme: true, // true = dark, else light
      commoditySpecs: [],
      companies: [{ id: 0, company_name: "N.A." }],
      companyParametersReady: false,
      countrySpecs: [],
      currencySpecs: [],
      ignoreVuex: false,
      linkList: ["Currencies", "Commodities"],
      optionTypes: [],
      processData: null,
      root: document.documentElement,
      selectedNavLink: 0,
      user: [],
    };
  },
  watch: {
    "$route.params.asset": {
      immediate: true,
      handler(newAssetType) {
        this.selectedNavLink = newAssetType === "commodity" ? 1 : 0 || 0;
      },
    },
    "$route.params.analysis": {
      immediate: true,
      handler(newAnalysis) {
        this.analysis = newAnalysis || null;
      },
    },
    "$route.params.productType": {
      immediate: true,
      handler(newProductType) {
        if (newProductType === "currency") {
          this.eventOnClientSelect(0);
          return;
        } else if (newProductType === "commodity") {
          this.eventOnClientSelect(1);
        }
      },
    },
  },
  created() {
    this.ignoreVuex = this.assets.includes(this.$route.params.productType);

    if (this.ignoreVuex) {
      this.selectedNavLink = Math.max(
        this.assets.indexOf(this.$route.params.productType),
        0,
      );
    } else {
      this.selectedNavLink = this.$store.getters[
        "user_preferences/analyticsChoices"
      ]?.selected_nav;
    }

    getOptionTypesData(this, "", false);

    const storePrefs = this.$store.getters["user_preferences/analyticsChoices"];

    const { analytics_preferences, prefsChanged } = initAnalyticsPreferences(
      storePrefs,
      storePrefs.selected_nav === 1 ? "commodity" : "currency",
    );

    this.analyticsPreferences = analytics_preferences;

    if (prefsChanged) {
      this.$store.dispatch(
        "user_preferences/updateAnalyticsChoices",
        analytics_preferences,
      );
      this.saveUserPref();
    }

    try {
      this.colorTheme = this.analyticsPreferences.color_theme;
      if (this.colorTheme === undefined) {
        this.colorTheme = true;
      }
      if (!this.colorTheme) {
        colorThemeChange(this.root, this.colorTheme);
        this.onUserColorTheme(this.colorTheme);
      }
    } catch (error) {
      console.error(`Error color theme ${error}`);
    }
    if (!this.ignoreVuex) {
      try {
        this.selectedNavLink = this.analyticsPreferences.selected_nav;
        if (
          this.selectedNavLink === undefined ||
          this.selectedNavLink == null
        ) {
          this.selectedNavLink = 0;
        }
      } catch (error) {
        this.selectedNavLink = 0;
        console.error(`Error selected nav link ${error}`);
      }
    }
    try {
      let cmpnies = this.$store.state.companies.all.items;
      cmpnies.forEach(function(d, i) {
        d.id = i;
      });
      Object.assign(this, {
        companies: cmpnies,
      });
    } catch (error) {
      console.error(`Error companies ${error}`);
    }
    this.processData = new ProcessData();
    try {
      this.user = this.$store.state.user.all.items;
    } catch (error) {
      console.error(`Error user ${error}`);
    }
    try {
      this.countrySpecs = this.$store.state.countries.all.items;
    } catch (error) {
      console.error(`Error countries ${error}`);
    }
    try {
      this.currencySpecs = this.$store.state.currencies.all.items;
    } catch (error) {
      console.error(`Error currencies ${error}`);
    }
    try {
      this.commoditySpecs = this.$store.state.commodities.all.items;
    } catch (error) {
      console.error(`Error commodities ${error}`);
    }
  },
  mounted() {
    this.getCompParams();
    this.intervalId = setInterval(this.saveUserPref, 600000);
  },
  beforeDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  },
  methods: {
    async getCompParams() {
      let compParam = await getCompanyParameters(this.companies, (partial) => {
        Object.assign(this.$store.state, {
          companyParameters: partial,
        });
        this.companyParametersReady = true;
      });
      Object.assign(this.$store.state, {
        companyParameters: compParam,
      });
      this.companyParametersReady = true;
    },
    onUserColorTheme(val) {
      this.colorTheme = val;
      this.analyticsPreferences.color_theme = val;
      colorThemeVuex(this, this.colorTheme);
      // saveUserPreferences(this);
    },
    eventOnClientSelect(idx) {
      this.analyticsPreferences.selected_nav = idx;
      assetSelectionVuex(this, idx);

      this.$store.dispatch(
        "user_preferences/updateAnalyticsChoices",
        this.analyticsPreferences,
      );
      this.selectedNavLink = idx;
    },
    saveUserPref() {
      // console.log("Selection saved at", new Date().toLocaleTimeString());
      this.$store.dispatch(
        "user_preferences/updateAnalyticsChoices",
        this.analyticsPreferences,
      );
      saveUserPreferences(this);
    },
  },
};
</script>

<style scoped></style>
